import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { defer, fromEvent, Observable, of, race, throwError } from 'rxjs';
import { finalize, map, mergeMap, shareReplay, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ScriptService {
  private static readonly loadedAttribute = 'data-app-script-loaded';

  private readonly document = inject(DOCUMENT);
  private readonly loadBySource$ = new Map<string, Observable<void>>();

  public loadBySource(source: string): Observable<void> {
    return defer(() => {
      const existingLoad$ = this.loadBySource$.get(source);
      if (existingLoad$) return existingLoad$;

      const existingScript = this.findScriptBySource(source);
      if (existingScript?.hasAttribute(ScriptService.loadedAttribute)) return of(void 0);

      const script = existingScript ?? this.createScript(source);
      const load$ = fromEvent(script, 'load').pipe(
        take(1),
        tap(() => script.setAttribute(ScriptService.loadedAttribute, 'true')),
        map((): void => void 0),
      );
      const error$ = fromEvent(script, 'error').pipe(
        take(1),
        tap(() => script.remove()),
        mergeMap(() => throwError(() => new Error(`Failed to load script: ${source}`))),
      );
      const scriptLoad$ = race(load$, error$).pipe(
        finalize(() => this.loadBySource$.delete(source)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

      this.loadBySource$.set(source, scriptLoad$);
      if (!existingScript) this.document.head.appendChild(script);

      return scriptLoad$;
    });
  }

  private createScript(source: string): HTMLScriptElement {
    const script = this.document.createElement('script');
    script.src = source;
    script.type = 'text/javascript';
    script.async = false;

    return script;
  }

  private findScriptBySource(source: string): HTMLScriptElement | null {
    return this.document.querySelector(`script[src="${source}"]`);
  }
}
